import { Suspense, useEffect } from 'react';
import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import ErrorBoundary from '@components/shared/ErrorBoundary/ErrorBoundary';
import RouteSkeleton from '@components/shared/RouteSkeleton/RouteSkeleton';
import { useAuthStore } from '@/stores/authStore';
import { canAccessRoute, getFirstAccessibleRoute } from '@/lib/routePermissions';

export const Route = createFileRoute('/w')({
  component: WorkpayLayout,
});

function WorkpayLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const organizationType = useAuthStore((s) => s.organizationType);
  const userType = useAuthStore((s) => s.userType);
  const permissions = useAuthStore((s) => s.permissions);

  // Org type guard — only WORKPAY admins can access /w routes
  useEffect(() => {
    if (!organizationType) return;
    if (userType === 'EMPLOYEE' || organizationType !== 'WORKPAY') {
      void navigate({ to: '/', replace: true });
    }
  }, [organizationType, userType, navigate]);

  // Permission guard — getFirstAccessibleRoute floors at /w/home so the
  // redirect target is always defined; users with zero matching permissions
  // land on Home rather than getting stuck.
  useEffect(() => {
    if (permissions.length === 0) return;

    if (!canAccessRoute(pathname, permissions)) {
      void navigate({ to: getFirstAccessibleRoute('/w', permissions), replace: true });
    }
  }, [pathname, permissions, navigate]);

  // Block render until the auth store is hydrated. `__root.tsx` calls
  // `setSession(data)` inside a `useEffect`, so on the render where the session
  // query resolves there is one tick during which `organizationType` and
  // `userType` are still null. The previous "block on wrong value" guards
  // returned a falsy check on null, letting the layout render its <Outlet />
  // for one frame — long enough for EMPLOYEE users to see and interact with
  // /w pages they should never reach.
  if (!organizationType || !userType) return null;
  if (organizationType !== 'WORKPAY' || userType === 'EMPLOYEE') return null;

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );
}
